import * as React from 'react';
import { store } from '../lib/store';
import SparkMD5 from 'spark-md5';
import { logger } from '../lib/logger';

// Note: currently all buyer-specific keys don't hold anything disruptive, in the future if
// we add something that needs to be blown away when a different buyer logs in, we'll need
// to add some logic to this file.
export const localStorageKeys = {
  sellerEmailHash: 'seller',
  smsNotificationConsent: 'smsNotificationConsent',
  videoBannerDismissed: 'videoBannerDismissed',
  pushNotificationNugget: 'pushNotificationNugget',
  pwaPrompt: 'pwaPrompt',
  score: 'score',
  lastShownModal: 'lastShownModal',
  toast: 'toast',
  bustScoreCache: 'bustScoreCache',
  joinedAt: 'joinedAt',
  changeNotice: 'changeNotice',
  recentPacketIds: 'rp',
  excludeCohortsInFolders: 'excludeCohortsInFolders'
};

type LocalStorageKey = keyof typeof localStorageKeys;

const persistentKeys: LocalStorageKey[] = [
  'pwaPrompt',
  'changeNotice',
  'recentPacketIds',
  'excludeCohortsInFolders'
];

// Keeps the hook state synced with a localStorage key, verifies the type of the value
// when reading from localStorage, setState can be used in its functional form to update
// based on previous value with a stable function just like React.useState
export const useLocalStorageState = <T>(
  key: string,
  defaultValue: T | (() => T),
  typeChecker?: (value: unknown) => value is T
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [state, setStateInternal] = React.useState<T | null>(() => {
    const value = defaultValue instanceof Function ? defaultValue() : defaultValue;
    return store.getItem(key, value, typeChecker);
  });

  const setState = React.useCallback(
    (value: T | ((prev: T) => T) | null): void => {
      setStateInternal(prev => {
        const processedValue = value instanceof Function ? value(prev) : value;
        store.setItem(key, processedValue);
        return processedValue;
      });
    },
    [key]
  );

  React.useDebugValue(`${key}: ${JSON.stringify(state)}`);

  return [state, setState];
};

// During login or registration, decide whethere we keep or reset localStorage
export const verifyLocalStorage = (email: string) => {
  const hash = SparkMD5.hash(email.toLowerCase());
  const storedHash = store.getItem('emailHash', '', s => typeof s === 'string');

  if (storedHash === hash) {
    logger('localstorage').info('Email hash matched, skipping localStorage reset');
    return;
  }

  logger('localstorage').info('Email hash mismatch, resetting localStorage');
  // blow away non-persistent keys if emails don't match
  Object.keys(localStorageKeys).forEach(k => {
    const key = k as LocalStorageKey;
    if (persistentKeys.includes(key)) return;
    store.setItem(localStorageKeys[key], null);
  });

  // set the new email hash
  store.setItem(localStorageKeys.sellerEmailHash, hash);
};
